import { useState, useEffect } from 'react';
import { memberProfileAPI } from '../services/api';
import { OnboardingProgressResponse } from '../types/memberProfile';

interface UseOnboardingOptions {
  memberProfileId?: string;
  autoFetch?: boolean;
}

export const useOnboarding = ({ memberProfileId, autoFetch = true }: UseOnboardingOptions = {}) => {
  const [onboardingData, setOnboardingData] = useState<OnboardingProgressResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOnboardingStatus = async (profileId?: string) => {
    const id = profileId || memberProfileId;
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await memberProfileAPI.getOnboardingStatus(id);
      setOnboardingData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch onboarding status');
      console.error('Error fetching onboarding status:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateOnboardingStep = async (
    step: 'billingSetup' | 'documentReview' | 'showInstructions',
    completed: boolean,
    notes?: string
  ) => {
    if (!memberProfileId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await memberProfileAPI.updateOnboardingStep(memberProfileId, {
        step,
        completed,
        notes,
      });
      setOnboardingData(response.data);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update onboarding step');
      console.error('Error updating onboarding step:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateOnboardingStatus = async (onboardingStatus: any) => {
    if (!memberProfileId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await memberProfileAPI.updateOnboardingStatus(memberProfileId, onboardingStatus);
      setOnboardingData(response.data);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update onboarding status');
      console.error('Error updating onboarding status:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const dismissOnboardingInstructions = async () => {
    return updateOnboardingStep('showInstructions', false);
  };

  const refreshOnboardingData = () => {
    if (memberProfileId) {
      fetchOnboardingStatus(memberProfileId);
    }
  };

  useEffect(() => {
    if (autoFetch && memberProfileId) {
      fetchOnboardingStatus(memberProfileId);
    }
  }, [memberProfileId, autoFetch]);

  // Computed properties
  const needsOnboarding = onboardingData ? !onboardingData.hasCompletedMandatory : false;
  const shouldShowInstructions = onboardingData ? onboardingData.shouldShowInstructions : false;
  const pendingStepsCount = onboardingData ? onboardingData.pendingSteps.length : 0;

  return {
    onboardingData,
    loading,
    error,
    needsOnboarding,
    shouldShowInstructions,
    pendingStepsCount,
    fetchOnboardingStatus,
    updateOnboardingStep,
    updateOnboardingStatus,
    dismissOnboardingInstructions,
    refreshOnboardingData,
  };
};